import React, { useState, useEffect } from "react";
import axios from "axios";

const StudentAchievement = () => {
  const sessionId = localStorage.getItem("currentSessionId");

  const [date, setDate] = useState("");
  const [competitionTitle, setCompetitionTitle] = useState("");
  const [place, setPlace] = useState("");
  const [schoolRank, setSchoolRank] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ================= FETCH ACHIEVEMENTS =================
  useEffect(() => {
    if (sessionId) fetchAchievements();
  }, [sessionId]);

  const fetchAchievements = async () => {
    try {
      const res = await axios.get(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/getAchievementsBySession/${sessionId}`
      );
      setAchievements(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  // ================= IMAGE HANDLER =================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file || null);
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  // ================= RESET FORM =================
  const resetForm = () => {
    setDate("");
    setCompetitionTitle("");
    setPlace("");
    setSchoolRank("");
    setDescription("");
    setImage(null);
    setPreview(null);
    setEditingId(null);
  };

  // ================= SUBMIT =================
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!sessionId) return alert("Session ID not found!");
  if (!date || !competitionTitle || !place || !schoolRank || !description ) {
    return alert("All fields are required!");
  }
setIsSubmitting(true);
  const formData = new FormData();
  formData.append("date", date);
  formData.append("competitionTitle", competitionTitle);
  formData.append("place", place);
  formData.append("schoolRank", schoolRank);
  formData.append("description", description);
  formData.append("sessionId", sessionId);
  formData.append("className", "1st");       // ✅ Add this
  formData.append("sectionName", "A");   // ✅ Add this
  if (image) formData.append("image", image);

  try {
    const url = editingId
      ? ` ${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/updateStudentAchievement/${editingId}`
      : ` ${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/createStudentAchievement`;

    const method = editingId ? "put" : "post";

    await axios[method](url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    alert(`Achievement ${editingId ? "updated" : "added"} successfully`);
    resetForm();
    fetchAchievements();
  } catch (err) {
    console.error("Submit error:", err.response?.data || err.message);
    alert(err.response?.data?.message || "Failed to submit");
  }finally {
    setIsSubmitting(false); // 🔥 stop loading
  }
};


  // ================= EDIT =================
  const handleEdit = (a) => {
    setDate(new Date(a.date).toISOString().slice(0, 10));
    setCompetitionTitle(a.competitionTitle);
    setPlace(a.place);
    setSchoolRank(a.schoolRank);
    setDescription(a.description);
    setPreview(a.image || null);
    setEditingId(a._id);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this achievement?")) return;

    try {
      await axios.delete(
        ` ${process.env.REACT_APP_API_BASE_URL}/api/studentAchievements/deleteStudentAchievement/${id}`,
        {params: { sessionId } }
      );
      alert("Achievement deleted successfully");
      fetchAchievements();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // ================= UI =================
  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h1 className="text-2xl font-bold mb-6">
        {editingId ? "Edit Student Achievement" : "Add Student Achievement"}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className="mb-1 font-semibold text-gray-700">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 font-semibold text-gray-700">Competition Title</label>
            <input
              type="text"
              value={competitionTitle}
              onChange={(e) => setCompetitionTitle(e.target.value)}
              placeholder="Enter title"
              className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 font-semibold text-gray-700">Place</label>
            <input
              type="text"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              placeholder="Enter place"
              className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-1 font-semibold text-gray-700">School Rank</label>
            <input
              type="text"
              value={schoolRank}
              onChange={(e) => setSchoolRank(e.target.value)}
              placeholder="Enter rank"
              className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
              required
            />
          </div>
        </div>

        <div className="flex flex-col">
          <label className="mb-1 font-semibold text-gray-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            placeholder="Write description..."
            className="border rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
            required
          />
        </div>

        <div className="flex flex-col">
          <label className="mb-1 font-semibold text-gray-700">Upload Image (Optional)</label>
          <input type="file" onChange={handleImageChange} />
          {preview && <img src={preview} alt="preview" className="mt-2 h-32 object-contain" />}
        </div>

        <div className="flex gap-4">
          <button
  type="submit"
  disabled={isSubmitting}
  className={`px-4 py-2 rounded text-white flex items-center justify-center gap-2
    ${isSubmitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}
  `}
>
  {isSubmitting ? (
    <>
      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
      {editingId ? "Updating..." : "Adding..."}
    </>
  ) : (
    editingId ? "Update Achievement" : "Add Achievement"
  )}
</button>

          <button
            type="button"
            onClick={resetForm}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Reset
          </button>
        </div>
      </form>

      <h2 className="text-2xl font-bold mt-10 mb-6">Achievements List</h2>

      {achievements.length === 0 ? (
        <p className="text-center text-gray-600">No achievements found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {achievements.map((a) => (
            <div key={a._id} className="p-5 bg-gray-50 border rounded shadow">
              <h3 className="text-xl font-semibold mb-2">{a.competitionTitle}</h3>
              <p className="mb-1">
                <strong>Date:</strong> {new Date(a.date).toLocaleDateString()}
              </p>
              <p className="mb-1">
                <strong>Place:</strong> {a.place}
              </p>
              <p className="mb-1">
                <strong>Rank:</strong> {a.schoolRank}
              </p>
              <p className="mb-2">{a.description}</p>

              {a.image && (
                <a
                  href={a.image}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline mb-2 inline-block"
                >
                  View Image
                </a>
              )}

              <div className="flex gap-3 mt-3">
                <button
                  onClick={() => handleEdit(a)}
                  className="bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-400"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(a._id)}
                  className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentAchievement;
